import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  API_KEY_GROUP,
  byEmployee,
  byModel,
  bySubscriber,
  dailyCost,
  niceTicks,
  totalsOf,
  type UsageGroup,
} from "../lib/aiUsageStats";
import { ApiRequestError, getAiUsage } from "../lib/client";
import { AI_USAGE_FEATURES, type AiUsageResponse } from "../lib/types";

type State =
  | { kind: "loading" }
  | { kind: "ready"; data: AiUsageResponse; loadedAt: Date }
  | { kind: "error"; message: string };

const RANGES = [7, 30, 90] as const;

const FEATURE_LABELS: Record<string, string> = {
  chat: "Chat",
  briefing: "Briefing",
  structured: "Structured",
  voice_call: "Voice call",
  sms: "SMS",
};

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const usdFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const compact = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});
const whole = new Intl.NumberFormat("en-US");

/** A single message can cost a fraction of a cent, which would round to $0.00. */
function usd(value: number): string {
  if (value > 0 && value < 0.01) return "<$0.01";
  return usdFormat.format(value);
}

function shortDay(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

// ---------------------------------------------------------------------------
// Tooltip
// ---------------------------------------------------------------------------

type Tip = { x: number; y: number; content: ReactNode } | null;

/**
 * One tooltip for the whole tab. A mark calls `show` with its content on hover,
 * and the tooltip follows the pointer inside `rootRef`.
 */
function useTooltip() {
  const rootRef = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<Tip>(null);

  function handlers(content: ReactNode) {
    return {
      onMouseMove: (event: React.MouseEvent) => {
        const rect = rootRef.current?.getBoundingClientRect();
        if (!rect) return;
        setTip({
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
          content,
        });
      },
      onMouseLeave: () => setTip(null),
    };
  }

  const element = tip ? (
    <div
      className="viz-tooltip"
      role="tooltip"
      style={{ left: tip.x + 14, top: tip.y + 14 }}
    >
      {tip.content}
    </div>
  ) : null;

  return { rootRef, handlers, element };
}

type TooltipHandlers = ReturnType<typeof useTooltip>["handlers"];

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

function StatTile({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: string;
}) {
  return (
    <div className="stat-tile">
      <span className="stat-label">{label}</span>
      <span className="stat-value">{value}</span>
      {detail && <span className="stat-detail">{detail}</span>}
    </div>
  );
}

function FeatureLegend() {
  return (
    <div className="viz-legend">
      {AI_USAGE_FEATURES.map((feature) => (
        <span key={feature} className="viz-legend-item">
          <span className={`viz-swatch feature-${feature}`} />
          {FEATURE_LABELS[feature]}
        </span>
      ))}
    </div>
  );
}

function FeatureTooltip({ group }: { group: UsageGroup }) {
  return (
    <>
      <strong>{group.label}</strong>
      <span>
        {usd(group.costUsd)} · {whole.format(group.events)} events
      </span>
      {AI_USAGE_FEATURES.filter((feature) => group.costByFeature[feature]).map(
        (feature) => (
          <span key={feature} className="viz-tooltip-row">
            <span className={`viz-swatch feature-${feature}`} />
            {FEATURE_LABELS[feature]}
            <span className="viz-tooltip-value">
              {usd(group.costByFeature[feature] ?? 0)}
            </span>
          </span>
        ),
      )}
    </>
  );
}

/**
 * Width of a bar scaled against `max`. The track keeps room for the value
 * label at the tip, so the longest bar never pushes it out of the cell.
 */
function barWidth(value: number, max: number) {
  const ratio = max > 0 ? value / max : 0;
  return { width: `calc((100% - 5.5rem) * ${ratio})` };
}

/** A horizontal bar, stacked by feature, scaled against `max`. */
function FeatureBar({
  group,
  max,
  tooltip,
}: {
  group: UsageGroup;
  max: number;
  tooltip: TooltipHandlers;
}) {
  return (
    <div className="bar-cell" {...tooltip(<FeatureTooltip group={group} />)}>
      <div className="bar-track">
        <div className="bar-stack" style={barWidth(group.costUsd, max)}>
          {AI_USAGE_FEATURES.filter(
            (feature) => group.costByFeature[feature],
          ).map((feature) => (
            <span
              key={feature}
              className={`bar-segment feature-${feature}`}
              style={{ flexGrow: group.costByFeature[feature] ?? 0 }}
            />
          ))}
        </div>
        <span className="bar-value">{usd(group.costUsd)}</span>
      </div>
    </div>
  );
}

/** Cost per day as columns, with a clean y-axis and a tooltip per day. */
function DailyChart({
  days,
  tooltip,
}: {
  days: { day: string; costUsd: number; events: number }[];
  tooltip: TooltipHandlers;
}) {
  const ticks = niceTicks(Math.max(0, ...days.map((d) => d.costUsd)));
  const top = ticks[ticks.length - 1] || 1;
  const labelled = new Set([
    0,
    Math.floor((days.length - 1) / 2),
    days.length - 1,
  ]);

  return (
    <div className="columns-chart">
      <div className="columns-axis">
        {ticks.map((tick) => (
          <span key={tick} style={{ bottom: `${(tick / top) * 100}%` }}>
            {usd(tick)}
          </span>
        ))}
      </div>
      <div className="columns-plot">
        {ticks.map((tick) => (
          <span
            key={tick}
            className={tick === 0 ? "columns-baseline" : "columns-grid"}
            style={{ bottom: `${(tick / top) * 100}%` }}
          />
        ))}
        <div className="columns-bands">
          {days.map((d) => (
            <div
              key={d.day}
              className="columns-band"
              {...tooltip(
                <>
                  <strong>{shortDay(d.day)}</strong>
                  <span>
                    {usd(d.costUsd)} · {whole.format(d.events)} events
                  </span>
                </>,
              )}
            >
              {d.costUsd > 0 && (
                <span
                  className="columns-bar"
                  style={{ height: `${(d.costUsd / top) * 100}%` }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="columns-x">
        {days.map((d, index) => (
          <span key={d.day}>{labelled.has(index) ? shortDay(d.day) : ""}</span>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab
// ---------------------------------------------------------------------------

/**
 * AI usage and cost from ps-main's usage ledger (`aiUsageEvents`).
 *
 * The overview spans every subscriber and ranks them by cost. Picking a
 * subscriber (a row here, or the subscriber in the top bar) breaks its usage
 * down by employee and by model. Picking an employee narrows the daily chart
 * to them. ps-main returns raw events, so every total here is computed in the
 * browser from one fetch.
 */
export function AiUsage({ subscriberId }: { subscriberId: string }) {
  const [days, setDays] = useState<number>(30);
  const [state, setState] = useState<State>({ kind: "loading" });
  const [focusSubscriber, setFocusSubscriber] = useState(subscriberId);
  const [focusEmployee, setFocusEmployee] = useState<string | null>(null);
  const tooltip = useTooltip();

  useEffect(() => {
    setFocusSubscriber(subscriberId);
    setFocusEmployee(null);
  }, [subscriberId]);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    getAiUsage(days)
      .then((data) => {
        if (!cancelled) setState({ kind: "ready", data, loadedAt: new Date() });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          kind: "error",
          message:
            err instanceof ApiRequestError
              ? err.message
              : "Failed to load AI usage.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  const events = state.kind === "ready" ? state.data.events : [];
  const subscribers = useMemo(() => bySubscriber(events), [events]);
  const subscriberEvents = useMemo(
    () => events.filter((event) => event.subscriberId === focusSubscriber),
    [events, focusSubscriber],
  );
  const employees = useMemo(
    () => byEmployee(subscriberEvents),
    [subscriberEvents],
  );
  const models = useMemo(() => byModel(subscriberEvents), [subscriberEvents]);

  const toolbar = (
    <div className="toolbar">
      <fieldset className="segmented" aria-label="Time range">
        {RANGES.map((range) => (
          <button
            key={range}
            type="button"
            className={`segmented-option${days === range ? " active" : ""}`}
            aria-pressed={days === range}
            onClick={() => setDays(range)}
          >
            Last {range} days
          </button>
        ))}
      </fieldset>
      {state.kind === "ready" && (
        <span className="muted">
          {whole.format(events.length)} events since{" "}
          {new Date(state.data.since).toLocaleString()}
        </span>
      )}
    </div>
  );

  if (state.kind === "loading") {
    return (
      <div className="panel">
        {toolbar}
        <span className="hint">Loading AI usage…</span>
      </div>
    );
  }
  if (state.kind === "error") {
    return (
      <div className="panel">
        {toolbar}
        <span className="hint error">{state.message}</span>
      </div>
    );
  }

  const totals = totalsOf(events);
  const focused = subscribers.find((group) => group.key === focusSubscriber);
  const employee = employees.find((group) => group.key === focusEmployee);
  const scopeEvents = employee
    ? subscriberEvents.filter(
        (event) => (event.userId ?? API_KEY_GROUP) === employee.key,
      )
    : focused
      ? subscriberEvents
      : events;
  const daily = dailyCost(
    scopeEvents,
    new Date(state.data.since),
    state.loadedAt,
  );
  const scopeLabel = employee
    ? `${employee.label} · ${focused?.label}`
    : (focused?.label ?? "All subscribers");
  const maxSubscriber = subscribers[0]?.costUsd ?? 0;
  const maxEmployee = employees[0]?.costUsd ?? 0;
  const maxModel = models[0]?.costUsd ?? 0;

  return (
    <div className="panel ai-usage" ref={tooltip.rootRef}>
      {toolbar}

      {state.data.truncated && (
        <div className="banner warn">
          This window holds more events than mechanicus reads at once, so the
          totals below undercount. Pick a shorter range.
        </div>
      )}

      <div className="stat-row">
        <StatTile label="Total cost" value={usd(totals.costUsd)} />
        <StatTile label="Events" value={whole.format(totals.events)} />
        <StatTile
          label="Subscribers"
          value={whole.format(subscribers.length)}
          detail="with any usage"
        />
        <StatTile
          label="Tokens"
          value={compact.format(totals.inputTokens + totals.outputTokens)}
          detail={`${compact.format(totals.inputTokens)} in · ${compact.format(totals.outputTokens)} out`}
        />
      </div>

      {events.length === 0 ? (
        <span className="hint">No AI usage recorded in this range.</span>
      ) : (
        <>
          <section className="viz-card">
            <header className="viz-card-header">
              <h3>Daily cost</h3>
              <span className="muted">{scopeLabel} · UTC days</span>
            </header>
            <DailyChart days={daily} tooltip={tooltip.handlers} />
          </section>

          <section className="viz-card">
            <header className="viz-card-header">
              <h3>Cost by subscriber</h3>
              <FeatureLegend />
            </header>
            <div className="table-wrap">
              <table className="bar-table">
                <thead>
                  <tr>
                    <th>Subscriber</th>
                    <th className="bar-col">Cost</th>
                    <th className="num">Events</th>
                    <th className="num">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((group) => (
                    <tr
                      key={group.key}
                      className={
                        group.key === focusSubscriber ? "selected" : undefined
                      }
                    >
                      <td>
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => {
                            setFocusSubscriber(
                              group.key === focusSubscriber ? "" : group.key,
                            );
                            setFocusEmployee(null);
                          }}
                        >
                          {group.label}
                        </button>
                      </td>
                      <td className="bar-col">
                        <FeatureBar
                          group={group}
                          max={maxSubscriber}
                          tooltip={tooltip.handlers}
                        />
                      </td>
                      <td className="num">{whole.format(group.events)}</td>
                      <td className="num">
                        {((group.costUsd / totals.costUsd) * 100).toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {focused ? (
            <>
              <section className="viz-card">
                <header className="viz-card-header">
                  <h3>
                    {focused.label} · cost by employee
                    <button
                      type="button"
                      className="link-button close-focus"
                      onClick={() => {
                        setFocusSubscriber("");
                        setFocusEmployee(null);
                      }}
                    >
                      clear
                    </button>
                  </h3>
                  <FeatureLegend />
                </header>
                <div className="stat-row">
                  <StatTile label="Cost" value={usd(focused.costUsd)} />
                  <StatTile
                    label="Events"
                    value={whole.format(focused.events)}
                  />
                  <StatTile
                    label="Employees"
                    value={whole.format(
                      employees.filter((group) => group.key !== API_KEY_GROUP)
                        .length,
                    )}
                    detail="with any usage"
                  />
                  {employees[0] && (
                    <StatTile
                      label="Top spender"
                      value={usd(employees[0].costUsd)}
                      detail={employees[0].label}
                    />
                  )}
                </div>
                <div className="table-wrap">
                  <table className="bar-table">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th className="bar-col">Cost</th>
                        <th className="num">Events</th>
                        <th className="num">Input</th>
                        <th className="num">Output</th>
                        <th className="num">Cache read</th>
                        <th>Top model</th>
                        <th>Last used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employees.map((group) => (
                        <tr
                          key={group.key}
                          className={
                            group.key === focusEmployee ? "selected" : undefined
                          }
                        >
                          <td>
                            <button
                              type="button"
                              className="link-button"
                              title="Show this employee in the daily chart"
                              onClick={() =>
                                setFocusEmployee(
                                  group.key === focusEmployee
                                    ? null
                                    : group.key,
                                )
                              }
                            >
                              {group.label}
                            </button>
                            {group.detail && (
                              <div className="nested">{group.detail}</div>
                            )}
                          </td>
                          <td className="bar-col">
                            <FeatureBar
                              group={group}
                              max={maxEmployee}
                              tooltip={tooltip.handlers}
                            />
                          </td>
                          <td className="num">{whole.format(group.events)}</td>
                          <td className="num">
                            {compact.format(group.inputTokens)}
                          </td>
                          <td className="num">
                            {compact.format(group.outputTokens)}
                          </td>
                          <td className="num">
                            {compact.format(group.cacheReadTokens)}
                          </td>
                          <td>
                            {group.topModel ? (
                              <code>{group.topModel}</code>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="nowrap">
                            {new Date(group.lastOccurredAt).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="viz-card">
                <header className="viz-card-header">
                  <h3>{focused.label} · cost by model</h3>
                </header>
                <div className="table-wrap">
                  <table className="bar-table">
                    <thead>
                      <tr>
                        <th>Model</th>
                        <th className="bar-col">Cost</th>
                        <th className="num">Events</th>
                        <th className="num">Input</th>
                        <th className="num">Output</th>
                      </tr>
                    </thead>
                    <tbody>
                      {models.map((group) => (
                        <tr key={group.key}>
                          <td>
                            <code>{group.label}</code>
                          </td>
                          <td className="bar-col">
                            <div
                              className="bar-cell"
                              {...tooltip.handlers(
                                <>
                                  <strong>{group.label}</strong>
                                  <span>
                                    {usd(group.costUsd)} ·{" "}
                                    {whole.format(group.events)} events
                                  </span>
                                </>,
                              )}
                            >
                              <div className="bar-track">
                                <div
                                  className="bar-stack"
                                  style={barWidth(group.costUsd, maxModel)}
                                >
                                  <span className="bar-segment single" />
                                </div>
                                <span className="bar-value">
                                  {usd(group.costUsd)}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="num">{whole.format(group.events)}</td>
                          <td className="num">
                            {compact.format(group.inputTokens)}
                          </td>
                          <td className="num">
                            {compact.format(group.outputTokens)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          ) : (
            <span className="hint">
              Pick a subscriber above to break its cost down by employee and by
              model.
            </span>
          )}
        </>
      )}

      {tooltip.element}
    </div>
  );
}
